import throttle_ from "lodash/throttle";

import type { Destructor } from "~/tools/destructor";
import { max } from "~/tools/max";

/**
 * 60 FPS
 */
const DEFAULT_THROTTLE = 1000 / 60;

export interface OnOptions<TNode> {
	/**
	 * @default document
	 */
	node?: TNode;

	/**
	 * AddEventListenerOptions.passive
	 *
	 * @default false
	 */
	passive?: boolean;

	/**
	 * AddEventListenerOptions.capture
	 *
	 * @default false
	 */
	capture?: boolean;

	/**
	 * Throttle interval in milliseconds.
	 *
	 * @default 60fps
	 */
	throttle?: number;
}

export function on<TEventName extends keyof DocumentEventMap, TNode extends Element | Document | Window = Document>(
	eventName: TEventName,
	handler: (element: TNode, event: DocumentEventMap[TEventName]) => void,
	{ node = document as TNode, throttle = DEFAULT_THROTTLE, passive = false, capture = false }: OnOptions<TNode> = {},
): Destructor {
	const throttleMs = max(0, throttle);

	const listener = (event: DocumentEventMap[TEventName]) => {
		handler(node, event);
	};

	if (throttleMs === 0) {
		node.addEventListener(eventName, listener, {
			passive,
			capture,
		});

		return () => {
			node.removeEventListener(eventName, listener);
		};
	}

	const throttledListener = throttle_(listener, throttleMs);

	node.addEventListener(eventName, throttledListener);

	return () => {
		node.removeEventListener(eventName, throttledListener);
		throttledListener.cancel();
	};
}
